<template>
  <!--
    全局登录引导弹窗（游客模式）
    - 由 useLoginGuide composable 全局状态驱动
    - 复用 login.vue 表单结构（手机号 + 密码）
    - 登录成功 → onLoginSuccess() 关闭弹窗 + 触发 retryFn 自动重试原请求
    - 登录失败 → toast 提示，弹窗不关，用户可重试
    - 「去注册」点击 → 跳转 /register 页面
  -->
  <van-popup
    v-model:show="localVisible"
    position="bottom"
    round
    teleport="body"
    :z-index="99"
    :close-on-click-overlay="false"
    :style="{ width: '100%', maxHeight: '90vh', overflow: 'auto' }"
    class="login-guide-popup"
  >
    <div class="popup-inner">
      <button class="popup-close" type="button" @click="onCloseClick" aria-label="关闭">
        <Icon icon="solar:close-circle-outline" :size="22" />
      </button>
      <h3 class="title">登录后继续操作</h3>
      <p class="subtitle">登录后即可享受完整功能、解锁全部权限</p>

      <van-form @submit="onSubmit" class="form">
        <van-cell-group inset>
          <van-field
            v-model="form.mobile"
            type="tel"
            label="手机号"
            placeholder="请输入手机号"
            maxlength="11"
            :rules="[
              { required: true, message: '请输入手机号' },
              { pattern: /^1[3-9]\d{9}$/, message: '手机号格式不正确' }
            ]"
          />
          <van-field
            v-model="form.password"
            type="password"
            label="密码"
            placeholder="请输入密码"
            :rules="[
              { required: true, message: '请输入密码' },
              { pattern: /^.{6,}$/, message: '密码至少 6 位' }
            ]"
          />
        </van-cell-group>
        <div class="btn-wrap">
          <van-button
            round
            block
            type="primary"
            native-type="submit"
            size="large"
            :loading="submitting"
            loading-text="登录中"
          >登录</van-button>
        </div>
      </van-form>

      <div class="go-footer">
        <span class="link" @click="goReset">忘记密码</span>
        <p class="go-register">
          还没账号？<span class="link" @click="goRegister">去注册 →</span>
        </p>
      </div>
    </div>
  </van-popup>
</template>

<script setup lang="ts">
import { reactive, ref, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { Icon } from '@iconify/vue'
import { showToast } from 'vant'
import { useUserStore } from '@/store'
import { useLoginGuide } from '@/composables/useLoginGuide'

const router = useRouter()
const userStore = useUserStore()
const { state, closeLoginGuide, onLoginSuccess } = useLoginGuide()

const form = reactive({ mobile: '', password: '' })
const submitting = ref(false)

// 本地 visible 双向绑定：写入时同步到全局 state
// 关闭来源区分：
//   用户点 X → onCloseClick() → closeLoginGuide() + router.replace('/')
//   登录成功 → onLoginSuccess() → closeLoginGuide()（不跳转）
//   其他（如 teleport 关闭）→ 不触发任何跳转
let closeSource: 'user-x' | 'success' | 'other' = 'other'

const localVisible = computed<boolean>({
  get: () => state.visible,
  set: (v) => {
    if (!v) {
      // onCloseClick 已直接调 closeLoginGuide（是否跳首页由 stayOnClose 决定）
      // 这里仅兜底 van-popup 内部触发的关闭（ESC 等）
      if (closeSource !== 'user-x' && closeSource !== 'success') {
        const stay = state.stayOnClose
        closeLoginGuide()
        if (!stay) router.replace('/').catch(() => {})
      }
      closeSource = 'other'
    }
  }
})

// 用户点击 X 关闭 → 关闭弹窗；默认回首页，stayOnClose 时留在当前页（游客可浏览页）
const onCloseClick = async () => {
  closeSource = 'user-x'
  const stay = state.stayOnClose
  state.visible = false
  closeLoginGuide()
  if (stay) {
    closeSource = 'other'
    return
  }
  try {
    await router.replace('/')
  } catch (_e) {
    // 兜底：hash router 直接写
    window.location.hash = '/'
  }
  closeSource = 'other'
}

// 登录成功
const onLoginSuccessHere = () => {
  closeSource = 'success'
  onLoginSuccess()
}

// 关闭弹窗时清空表单，避免下次复用残留
watch(localVisible, (v) => {
  if (!v) {
    form.mobile = ''
    form.password = ''
  }
})

const onSubmit = async () => {
  if (submitting.value) return
  submitting.value = true
  try {
    await userStore.loginAndFetch(form.mobile.trim(), form.password)
    showToast({ type: 'success', message: '登录成功' })
    // 先捕获「登录后留在当前页」标记（onLoginSuccess 会复位全局状态）
    const stayAfterLogin = state.stayAfterLogin
    // 关闭弹窗 + resolve pendingPromise（触发 request.ts 链式重试原请求 / await 方续跑流程）
    onLoginSuccessHere()
    if (stayAfterLogin) {
      // 调用方（如 AI 红娘介绍页）会自行续跑业务流程，不做统一跳转
      return
    }
    // 默认：登录成功统一跳首页，避免停留在可能触发 40101 的页面导致循环弹窗
    await router.replace('/home').catch(() => {})
  } catch (_e) {
    // 失败 toast 由 request 统一处理，弹窗不关，用户可重试
  } finally {
    submitting.value = false
  }
}

const goRegister = () => {
  closeLoginGuide()
  router.push({ path: '/register' }).catch(() => {})
}

// 忘记密码 → 复用既有重置密码页（与登录页 toReset 同一路径）
const goReset = () => {
  closeLoginGuide()
  router.push({ path: '/reset-password' }).catch(() => {})
}
</script>

<style lang="scss" scoped>
/* 层级由 van-popup 官方 :z-index="99" 统一管理（遮罩自动 98），
   禁止在 CSS 内另设 z-index，避免遮罩盖住弹窗导致黑屏 */
.popup-inner {
  padding: 20px var(--page-padding-x) calc(env(safe-area-inset-bottom) + 16px);
  position: relative;
}
.popup-close {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  color: #999;
  cursor: pointer;
  border-radius: 50%;
  &:active { background: var(--color-bg-subtle); }
}
.title {
  margin: 0 0 8px;
  font-size: 18px;
  font-weight: 600;
  text-align: center;
  color: var(--color-text-primary);
}
.subtitle {
  margin: 0 0 20px;
  font-size: var(--font-size-sm);
  color: var(--color-text-tertiary);
  text-align: center;
  letter-spacing: 1px;
}
.form {
  :deep(.van-cell-group--inset) {
    border-radius: var(--radius-md);
    background: var(--color-bg-card);
  }
}
.btn-wrap {
  margin: var(--space-xxl) var(--space-md) var(--space-md);
}
.go-footer {
  margin-top: var(--space-xxl);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 0 var(--space-md);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  line-height: 1.6;
}
.go-register {
  margin: 0;
  text-align: right;
}
.go-footer > .link { margin-left: 0; }
.link {
  color: var(--color-brand);
  font-weight: var(--font-weight-medium);
  margin-left: var(--space-xs);
  cursor: pointer;
}
</style>
